import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { Features } from "../../../Enums/companyStatus";
import { hasPermission } from "../../../Helpers/authHelpers";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import {
  VoicesCompanyPage,
  VoicesPerson,
} from "../../../Models/voicesProfiles";
import {
  useDeleteVoicesCompanyPage,
  useVoicesBufferChannels,
  useVoicesCompanyPages,
  useVoicesPeople,
} from "../../../Query/useVoicesProfiles";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesCompanyPageDialog from "./VoicesCompanyPageDialog";
import VoicesPersonDialog from "./VoicesPersonDialog";

const cellSx = { color: color.gray900 };

const VoicesPeopleAndPages: React.FC = () => {
  const { t } = useTranslation("voicesProfiles");
  const { colors: workspace } = useWorkspaceColors();
  const canManage = hasPermission(Features.VOICES_MARKETING);
  const { data: people = [], isLoading: peopleLoading } = useVoicesPeople();
  const { data: pages = [], isLoading: pagesLoading } = useVoicesCompanyPages();
  const deletePage = useDeleteVoicesCompanyPage();
  const { data: bufferChannels } = useVoicesBufferChannels();
  const channels = bufferChannels?.channels ?? [];

  const channelLabel = (channelId: string | null | undefined) => {
    if (!channelId) return "–";
    const channel = channels.find((item) => item.id === channelId);
    return channel?.display_name ?? channel?.name ?? t("bufferConnected");
  };

  const [editedPerson, setEditedPerson] = useState<VoicesPerson | null>(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [editedPage, setEditedPage] = useState<VoicesCompanyPage | null>(null);
  const [pageToDelete, setPageToDelete] = useState<VoicesCompanyPage | null>(
    null,
  );

  const openPageDialog = (page: VoicesCompanyPage | null) => {
    setEditedPage(page);
    setPageDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!pageToDelete) return;
    deletePage.mutate(pageToDelete.id, {
      onSuccess: () => setPageToDelete(null),
    });
  };

  const roleChip = (label: string) => (
    <Chip
      key={label}
      label={label}
      size="small"
      sx={{
        mr: 0.5,
        backgroundColor: color.orange50,
        border: `1px solid ${color.orange200}`,
        color: color.gray900,
      }}
    />
  );

  return (
    <>
      <VoicesCard title={t("people")} subtitle={t("peopleSubtitle")}>
        {!peopleLoading && !people.length ? (
          <Typography variant="body2" sx={{ color: color.gray500 }}>
            {t("noPeople")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("name")}</TableCell>
                <TableCell>{t("roles")}</TableCell>
                <TableCell>{t("bufferChannel")}</TableCell>
                <TableCell>{t("weeklyLimit")}</TableCell>
                <TableCell>{t("blockedTerms")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((person) => (
                <TableRow key={person.user_id} hover>
                  <TableCell sx={cellSx}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {person.name} {person.surname}
                    </Typography>
                    <Typography variant="caption" sx={{ color: color.gray500 }}>
                      {person.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {person.is_posting && roleChip(t("rolePosting"))}
                    {person.is_marketing && roleChip(t("roleMarketing"))}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {channelLabel(person.profile?.buffer_channel_id)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {person.profile?.weekly_limit ?? "–"}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {person.profile?.blocked_terms.length || "–"}
                  </TableCell>
                  <TableCell align="right">
                    {canManage && person.is_posting && (
                      <IconButton
                        size="small"
                        onClick={() => setEditedPerson(person)}
                        sx={{ color: workspace.accent }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </VoicesCard>

      <VoicesCard
        title={t("companyPages")}
        subtitle={t("companyPagesSubtitle")}
        action={
          canManage && (
            <ComoButton
              startIcon={<AddIcon />}
              onClick={() => openPageDialog(null)}
            >
              {t("newCompanyPage")}
            </ComoButton>
          )
        }
      >
        {!pagesLoading && !pages.length ? (
          <Typography variant="body2" sx={{ color: color.gray500 }}>
            {t("noCompanyPages")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("companyPageName")}</TableCell>
                <TableCell>{t("bufferChannel")}</TableCell>
                <TableCell>{t("weeklyLimit")}</TableCell>
                <TableCell>{t("blockedTerms")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id} hover>
                  <TableCell sx={{ ...cellSx, fontWeight: 500 }}>
                    {page.name}
                  </TableCell>

                  <TableCell sx={cellSx}>
                    {channelLabel(page.buffer_channel_id)}
                  </TableCell>
                  <TableCell sx={cellSx}>{page.weekly_limit ?? "–"}</TableCell>
                  <TableCell sx={cellSx}>
                    {page.blocked_terms.length || "–"}
                  </TableCell>
                  <TableCell align="right">
                    {canManage && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          size="small"
                          onClick={() => openPageDialog(page)}
                          sx={{ color: workspace.accent }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setPageToDelete(page)}
                          sx={{ color: color.gray500 }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </VoicesCard>

      <VoicesPersonDialog
        person={editedPerson}
        onClose={() => setEditedPerson(null)}
      />
      <VoicesCompanyPageDialog
        open={pageDialogOpen}
        page={editedPage}
        onClose={() => setPageDialogOpen(false)}
      />
      <Dialog open={!!pageToDelete} onClose={() => setPageToDelete(null)}>
        <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>
          {t("deleteCompanyPage")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: color.gray700 }}>
            {t("deleteCompanyPageConfirm", { name: pageToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPageToDelete(null)}
            sx={{ color: color.gray600, textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <ComoButton onClick={confirmDelete} disabled={deletePage.isPending}>
            {t("delete")}
          </ComoButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoicesPeopleAndPages;
